<script setup lang="ts">
import { onBeforeUnmount, onMounted, useTemplateRef } from 'vue';
import { debounceTime, fromEvent } from 'rxjs';
import type { Subscription } from 'rxjs';

import type { DropdownOpeningDirection } from '../selects/models/common.models';

import type { AppInputTheme } from './AppInput.model';

interface AppInputProps {
  inputText: string;
  placeholder: string;
  theme: AppInputTheme;
  shoulDisplayMobileVersion?: boolean;
  maxLength?: number;
  debounceDelay?: number;
  disable?: boolean;
  openingDirection?: DropdownOpeningDirection | undefined;
  shouldFocusOnInit?: boolean;
}

const props = defineProps<AppInputProps>();

interface AppInputEmits {
  (event: 'text-changed', value: string): void;
  (event: 'focus-changed', value: boolean): void;
  (event: 'keydown', value: KeyboardEvent): void;
  (event: 'init', value: HTMLInputElement): void;
}
const emit = defineEmits<AppInputEmits>();

const inputRef = useTemplateRef('inputRef');
let inputSubscription: Subscription | null = null;
const DEFAULT_DEBOUNCE_TIME = 0;

function focus(): void {
  setTimeout(() => {
    if (inputRef.value?.value) {
      inputRef.value.setSelectionRange(
        inputRef.value.value.length,
        inputRef.value.value.length
      );
      inputRef.value.scrollLeft = inputRef.value.scrollWidth;
    }
  }, 0);

  emit('focus-changed', true);
}

onMounted(() => {
  if (inputRef.value) {
    emit('init', inputRef.value);
    if (props.shouldFocusOnInit) {
      inputRef.value.focus();
    }

    inputSubscription = fromEvent(inputRef.value, 'input')
      .pipe(debounceTime(props.debounceDelay ?? DEFAULT_DEBOUNCE_TIME))
      .subscribe((event) => {
        const target = event.target as HTMLInputElement;
        emit('text-changed', target.value);
      });
  }
});

onBeforeUnmount(() => {
  if (inputSubscription) {
    inputSubscription.unsubscribe();
  }
});
</script>

<template>
  <div
    class="app-input"
    :class="[
      props.theme,
      props.openingDirection ? props.openingDirection : '',
      {
        'app-mobile-input': props.shoulDisplayMobileVersion,
        disable: props.disable,
      },
    ]"
  >
    <slot name="left-icon" />
    <input
      ref="inputRef"
      :value="props.inputText"
      type="text"
      :maxlength="props.maxLength"
      :placeholder="props.placeholder"
      :disabled="props.disable"
      @focus="focus"
      @blur="emit('focus-changed', false)"
      @keydown="(event) => emit('keydown', event)"
    />
    <slot name="right-icon" />
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/fonts' as *;
@use '../../styles/media' as *;

.app-input {
  position: relative;
  box-sizing: border-box;
  border-radius: var(--border-radius-12);
  overflow: hidden;
  user-select: none;
  display: flex;
  align-items: center;
  margin: 0;
  height: 44px;
  padding: 0 0 0 var(--spacing-16);
  width: 100%;
  gap: var(--spacing-8);

  &:has(input:focus) {
    box-shadow: inset 0 0 0 1px var(--color-soft-black);
  }

  &.app-mobile-input {
    transition: all var(--transition-very-short);
    min-width: 180px;
    width: 180px;

    &:has(input:focus) {
      min-width: 280px;
    }
  }

  &.light {
    background: var(--color-white);
  }

  &.dark {
    background: var(--color-neutral-30);
  }

  &.neutral {
    background: var(--color-white);
    border: 1px solid var(--color-neutral-40);
    border-radius: var(--border-radius-8);
    transition: border-radius var(--transition-short);
    height: 56px;

    &:has(input:focus) {
      border: 1px solid var(--color-soft-black);
      box-shadow: none;
    }

    input {
      @include font-label-m-light;
      color: var(--color-neutral-70);
      &::placeholder {
        color: var(--color-neutral-70);
        @include font-label-m-light;
      }
    }

    &.disable {
      border: 1px solid var(--color-neutral-30);
      color: var(--color-neutral-30);
      input {
        color: var(--color-neutral-30);

        &::placeholder {
          color: var(--color-neutral-30);
        }
      }
    }

    @include on-hover {
      &:not(.disable):not(:has(input:focus)) {
        background-color: var(--color-neutral-10);
      }
    }
  }

  &.open-above {
    border-radius: 0 0 var(--border-radius-8) var(--border-radius-8);
  }

  &.open-below {
    border-radius: var(--border-radius-8) var(--border-radius-8) 0 0;
  }

  input {
    background: inherit;
    color: var(--color-neutral-70);
    width: 100%;
    @include font-label-m-light;
    border-radius: var(--border-radius-12);
    border: none;
    outline: 0;
    box-shadow: none;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    margin-right: var(--spacing-16);
    padding-right: var(--spacing-24);

    &:focus {
      &::placeholder {
        color: var(--color-neutral-50);
      }
    }

    &::placeholder {
      color: var(--color-neutral-70);
      @include font-label-m-light;
    }
  }
}
</style>
